import React, { useState, useEffect } from "react";
import { createUser, fetchCustomers } from "./api/users";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Customer, Role } from "types/settings";
import { MultiSelectCombobox } from "../../../components/ui/multi-select-combobox";

interface CreateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserCreated: () => void;
  roles: Role[];
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ 
  open, 
  onOpenChange, 
  onUserCreated, 
  roles 
}) => {
  const [creating, setCreating] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [customers, setCustomers] = useState<Customer[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    mobile_no: "",
    email: "",
    role_id: "",
    user_type: "Customer",
    cust_ids: [] as string[],
  });

  useEffect(() => {
    if (open) {
      resetForm();
      loadCustomers();
    }
  }, [open]);

  const loadCustomers = async () => {
    try {
        const res = await fetchCustomers();
        setCustomers(res);
    } catch (err) {
        console.error("Failed to load customers", err);
    }
    };

  const resetForm = () => {
    setCreateError("");
    setValidationErrors({});
    setFormData({
      name: "",
      mobile_no: "",
      email: "",
      role_id: "",
      user_type: "Customer",
      cust_ids: [],
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }

    if (field === "mobile_no" && value) {
      const mobileRegex = /^\d{10}$/;
      if (value.length > 0 && !mobileRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          mobile_no: value.length !== 10 ? "Must be exactly 10 digits" : "Only numbers allowed"
        }));
      }
    }

    if (field === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value.length > 0 && !emailRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          email: "Please enter a valid email address"
        }));
      }
    }
  };

  const toggleCustomer = (custId: string) => {
    setFormData((prev) => {
      if (prev.user_type === "Customer") {
        return { ...prev, cust_ids: [custId] };
      }

      const exists = prev.cust_ids.includes(custId);

      return {
        ...prev,
        cust_ids: exists
          ? prev.cust_ids.filter((id) => id !== custId)
          : [...prev.cust_ids, custId],
      };
    });
  };

  const removeCustomer = (custId: string) => {
    setFormData((prev) => ({
      ...prev,
      cust_ids: prev.cust_ids.filter((id) => id !== custId),
    }));
  };

  const handleCreateUser = async () => {
    try {
      setCreating(true);
      setCreateError("");

      if (!formData.name.trim()) {
        setCreateError("Name is required");
        return;
      }

      if (!formData.mobile_no.trim()) {
        setCreateError("Mobile number is required");
        return;
      }
      const mobileRegex = /^\d{10}$/;
      if (!mobileRegex.test(formData.mobile_no.trim())) {
        setCreateError("Mobile number must be exactly 10 digits");
        return;
      }

      if (!formData.email.trim()) {
        setCreateError("Email is required");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setCreateError("Please enter a valid email address");
        return;
      }

      if (formData.cust_ids.length === 0) {
        setCreateError("Customer selection is required");
        return;
      }

      if (!formData.role_id) {
        setCreateError("Role is required");
        return;
      }

      const userData = {
        name: formData.name.trim(),
        mobile_no: formData.mobile_no.trim(),
        email: formData.email.trim(),
        role_id: parseInt(formData.role_id),
        user_type: formData.user_type,
        cust_ids: formData.cust_ids,
      };

      await createUser(userData);

      // Notify parent component to refresh users list
      if (onUserCreated) {
        onUserCreated();
      }

      onOpenChange(false);
    } catch (err: any) {
      console.error("Error creating user:", err);
      let errorMessage = "Failed to create user";
      
      if (err?.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
        else if (Array.isArray(err.response.data.detail) && err.response.data.detail.length > 0) {
          const firstError = err.response.data.detail[0];
          errorMessage = firstError.msg || "Validation error";
        }
      } else if (err?.response?.status === 400 && err?.response?.data) {
        errorMessage = err.response.data.detail || err.response.data.message || "Bad request";
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setCreateError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const selectedCustomers = customers.filter((c) =>
    formData.cust_ids.includes(c.cust_id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base font-medium">Create New User</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1 space-y-4 pb-1">
          {createError && (
            <Alert variant="destructive">
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter full name"
              autoFocus={false}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile" className="text-sm font-medium">Mobile Number *</Label>
            <Input
              id="mobile"
              value={formData.mobile_no}
              onChange={(e) => handleInputChange('mobile_no', e.target.value)}
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
              className={validationErrors.mobile_no ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.mobile_no && (
              <p className="text-sm text-red-600">{validationErrors.mobile_no}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter email address"
              className={validationErrors.email ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.email && (
              <p className="text-sm text-red-600">{validationErrors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>User Type *</Label>
            <Select
              value={formData.user_type}
              onValueChange={(value) =>
                handleInputChange("user_type", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select user type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Customer">Customer</SelectItem>
                <SelectItem value="Internal">Internal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>
              {formData.user_type === "Customer"
                ? "Customer *"
                : " Customers *"}
            </Label>

            <MultiSelectCombobox
              options={customers.map(c => ({
                value: c.cust_id,
                label: c.cust_name,
              }))}
              value={formData.cust_ids}
              onValueChange={(values) => {
                if (formData.user_type === "Customer") {
                  setFormData(prev => ({
                    ...prev,
                    cust_ids: values.length ? [values[values.length - 1]] : [],
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    cust_ids: values,
                  }));
                }
              }}
              placeholder="Select customers"
              searchPlaceholder="Search customers..."
              emptyText="No customers found"
            />
            {selectedCustomers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedCustomers.map((cust) => (
                  <span
                    key={cust.cust_id}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                  >
                    {cust.cust_name}
                    <button
                      type="button"
                      onClick={() => removeCustomer(cust.cust_id)}
                      className="ml-1 text-muted-foreground hover:text-destructive"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="role" className="text-sm font-medium">Role *</Label>
            <Select value={formData.role_id} onValueChange={(value) => handleInputChange('role_id', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.role_id} value={role.role_id.toString()}>
                    {role.role_desc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateUser} disabled={creating}>
            {creating ? 'Creating...' : 'Create User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;
